import { initData } from "@telegram-apps/sdk-react"
import axios, { AxiosInstance } from "axios"

interface TokenConfig {
  getTokenUrl: string
  storageKey: string
  getTokenParams?: Record<string, any>
  isPost?: boolean
  dataPath?: string
}

class AxiosInstanceManager {
  private readonly instance: AxiosInstance
  private tokenConfig: TokenConfig
  private tokenPromise: Promise<string | null> | null = null

  constructor(baseURL: string, tokenConfig: TokenConfig) {
    this.tokenConfig = tokenConfig
    this.instance = axios.create({ baseURL })
    this.setupInterceptors()
  }

  public getInstance(): AxiosInstance {
    return this.instance
  }

  private async getToken(): Promise<string | null> {
    if (this.tokenPromise) return this.tokenPromise

    this.tokenPromise = (async () => {
      try {
        let response
        const currentInitData = initData.raw()
        const userData = initData.user()

        if (this.tokenConfig.isPost) {
          // Create a new copy of params to avoid modifying the original
          const params = { ...this.tokenConfig.getTokenParams }

          // If we have dataTma parameter and currentInitData is available, update it
          if (params && "dataTma" in params && currentInitData)
            params.dataTma = currentInitData

          response = await axios.post(this.tokenConfig.getTokenUrl, params)
        } else
          response = await axios.get(this.tokenConfig.getTokenUrl, {
            headers: { Authorization: `Bearer ${currentInitData}` },
          })

        const token = this.tokenConfig.dataPath
          ? this.tokenConfig.dataPath
              .split(".")
              .reduce((obj, key) => obj[key], response.data)
          : response.data.token

        if (userData?.id)
          sessionStorage.setItem(
            `${userData.id}_${this.tokenConfig.storageKey}`,
            token
          )

        this.tokenPromise = null
        return token
      } catch (error) {
        console.error(`Error fetching ${this.tokenConfig.storageKey}:`, error)
        if (axios.isAxiosError(error)) {
          console.error("Response data:", error.response?.data)
          console.error("Status:", error.response?.status)
        }
        this.tokenPromise = null
        return null
      }
    })()

    return this.tokenPromise
  }

  private getTokenFromStorage(): string | null {
    const userData = initData.user()

    return userData?.id
      ? sessionStorage.getItem(`${userData.id}_${this.tokenConfig.storageKey}`)
      : null
  }

  private setupInterceptors() {
    this.instance.interceptors.request.use(
      async (config) => {
        if (config.url === "/token") return config

        let token = this.getTokenFromStorage()

        if (!token) {
          token = await this.getToken()
          if (!token)
            throw new Error(`No ${this.tokenConfig.storageKey} available`)
        }

        config.headers.Authorization = `Bearer ${token}`
        return config
      },
      (error) => Promise.reject(error)
    )

    this.instance.interceptors.response.use(
      (response) => {
        return this.tokenConfig.dataPath ? response.data.data : response.data
      },
      async (error) => {
        const { response } = error
        if (response && (response.status === 403 || response.status === 406))
          try {
            const newToken = await this.getToken()
            if (!newToken)
              return Promise.reject(
                new Error(`Failed to refresh ${this.tokenConfig.storageKey}`)
              )

            error.config.headers.Authorization = `Bearer ${newToken}`
            return this.instance.request(error.config)
          } catch (tokenError) {
            return Promise.reject(tokenError)
          }

        throw error
      }
    )
  }
}

const mainTokenConfig: TokenConfig = {
  getTokenUrl: `${import.meta.env.VITE_API}/token`,
  storageKey: "authToken",
  isPost: false,
}

const catalogTokenConfig: TokenConfig = {
  getTokenUrl: `${import.meta.env.VITE_CORE_API_URL}/auth/user-tma`,
  storageKey: "catalogToken",
  isPost: true,
  dataPath: "data.token",
  getTokenParams: {
    applicationId: import.meta.env.VITE_CORE_API_APP_ID,
    dataTma: "",
  },
}

const mainAxiosManager = new AxiosInstanceManager(
  import.meta.env.VITE_API,
  mainTokenConfig
)

const catalogAxiosManager = new AxiosInstanceManager(
  import.meta.env.VITE_CORE_API_URL,
  catalogTokenConfig
)

export const axiosInstance = mainAxiosManager.getInstance()
export const axiosCatalogInstance = catalogAxiosManager.getInstance()

export const getTokenFromStorage = (key: string) => {
  const userData = initData.user()

  return userData?.id ? sessionStorage.getItem(`${userData.id}_${key}`) : null
}
